// KeepFree — "How Bloom stays free": why there are ads and Amazon links, our mission, and a feedback form.
// Opened from any "Why ads?" link through openKeepFree(); the module name avoids ad-block filename rules.
import React, { useEffect, useState } from "react";
import { useLang } from "./i18n";

const API_BASE = "/api/bloom";
const OPEN_EVENT = "bloom:keepfree";

export function openKeepFree() {
    window.dispatchEvent(new Event(OPEN_EVENT));
}

// Small inline link used next to "Sponsored" labels and the Amazon disclosure.
export function WhyAdsLink({ className }: { className?: string }) {
    const { t } = useLang();
    return (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); openKeepFree(); }}
            className={"font-semibold text-green-700 underline decoration-dotted underline-offset-2 hover:text-green-900 " + (className || "")}
        >
            {t.whyAds}
        </button>
    );
}

function HeartIcon({ className }: { className?: string }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
            <path d="M12 21s-7.5-4.6-9.6-9.2C.9 8.4 3 5 6.4 5c2 0 3.5 1.1 4.3 2.5h2.6C14.1 6.1 15.6 5 17.6 5 21 5 23.1 8.4 21.6 11.8 19.5 16.4 12 21 12 21Z" />
        </svg>
    );
}

export default function KeepFree() {
    const { t, lang } = useLang();
    const [open, setOpen] = useState(false);
    const [msg, setMsg] = useState("");
    const [email, setEmail] = useState("");
    const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");

    useEffect(() => {
        const onOpen = () => setOpen(true);
        window.addEventListener(OPEN_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_EVENT, onOpen);
    }, []);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener("keydown", onKey);
        };
    }, [open]);

    if (!open) return null;

    const send = async () => {
        if (msg.trim().length < 3 || state === "sending") return;
        setState("sending");
        try {
            const res = await fetch(`${API_BASE}/feedback`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ msg, email, topic: "ads", l: lang }),
            });
            const ctype = res.headers.get("content-type") || "";
            if (!res.ok || !ctype.includes("application/json")) throw new Error(String(res.status));
            setState("done");
        } catch (e) {
            setState("error");
        }
    };

    return (
        <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label={t.fundTitle}>
            <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm animate-in fade-in duration-200" onClick={() => setOpen(false)} />
            <div
                className={
                    "absolute inset-x-0 bottom-0 flex max-h-[92vh] flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl " +
                    "animate-in max-md:slide-in-from-bottom duration-300 " +
                    "md:inset-auto md:left-1/2 md:top-1/2 md:max-h-[88vh] md:w-[560px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-3xl md:zoom-in-95"
                }
            >
                <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-gray-200 md:hidden" />
                <div className="relative bg-gradient-to-br from-green-50 via-white to-emerald-50 px-6 pb-4 pt-5">
                    <button
                        onClick={() => setOpen(false)}
                        aria-label={t.close}
                        className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-white text-xl text-gray-500 shadow ring-1 ring-gray-100 transition hover:rotate-90 hover:text-gray-900"
                    >
                        &times;
                    </button>
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-green-600 text-white shadow-lg shadow-green-600/25">
                        <HeartIcon className="h-5 w-5" />
                    </span>
                    <h2 className="mt-3 pr-10 text-xl font-extrabold text-gray-900">{t.fundTitle}</h2>
                </div>

                <div className="flex-1 space-y-3 overflow-y-auto px-6 pb-5 text-sm leading-relaxed text-gray-700">
                    <p>{t.fundP1}</p>
                    <p>{t.fundP2}</p>
                    <p>{t.fundP3}</p>
                    <div className="rounded-2xl bg-green-50 p-4 ring-1 ring-green-100">
                        <p className="text-xs font-bold uppercase tracking-widest text-green-700">{t.fundMissionTitle}</p>
                        <p className="mt-2 text-[15px] font-medium leading-relaxed text-gray-800">{t.fundMission}</p>
                    </div>

                    {state === "done" ? (
                        <p className="rounded-2xl bg-emerald-50 p-4 text-center font-semibold text-emerald-700 ring-1 ring-emerald-100">{t.fundThanks}</p>
                    ) : (
                        <div className="rounded-2xl p-4 ring-1 ring-gray-200">
                            <p className="font-semibold text-gray-900">{t.fundFeedback}</p>
                            <textarea
                                value={msg}
                                onChange={(e) => setMsg(e.target.value.slice(0, 1000))}
                                rows={3}
                                placeholder={t.fundMsgPlaceholder}
                                className="mt-2 w-full resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-green-500 focus:outline-none"
                            />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value.slice(0, 120))}
                                placeholder={t.fundEmailPlaceholder}
                                className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-800 focus:border-green-500 focus:outline-none"
                            />
                            <div className="mt-2 flex items-center justify-between gap-2">
                                <span className="text-[11px] text-red-600">{state === "error" ? t.reportError : ""}</span>
                                <button
                                    type="button"
                                    onClick={send}
                                    disabled={state === "sending" || msg.trim().length < 3}
                                    className="rounded-xl bg-gray-900 px-4 py-2 text-xs font-bold text-white transition hover:bg-black disabled:opacity-40"
                                >
                                    {t.send}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="border-t border-gray-100 bg-white p-4">
                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="block w-full rounded-xl bg-green-600 py-3 text-center text-sm font-bold text-white shadow-lg shadow-green-600/20 transition hover:bg-green-700"
                    >
                        {t.fundOk}
                    </button>
                </div>
            </div>
        </div>
    );
}
